import React, { useState, useRef, memo, forwardRef, useImperativeHandle } from 'react';
import { oneOf, string, element, bool, node, func, oneOfType, number } from 'prop-types';
import classnames from 'classnames';

import { InputBase, IconButton, Menu, MenuItem } from '@material-ui/core';
import { Clear, ExpandMore, ExpandLess } from '@material-ui/icons';

import shortid from 'shortid';
import { CSSTransition } from 'react-transition-group';
import useDelay from 'common/hooks/useDelay';
import { NumericFormat } from 'react-number-format';
import { useTranslation } from 'react-i18next';
import { getDecimalSeparator } from 'views/SpecificationProductionDetails/SpecificationCosts/SpecificationCostsView/CostDetailsModals/commons';
import HelperText from './HelperText';
import Label from './Label';
import Loader from './Loader';

import useStyles from './useStyles';

const ThemedTextField = forwardRef(
  (
    {
      MenuStyles,
      autoFocus = false,
      children,
      className = '',
      clearIconTabIndex = 1,
      disabled = false,
      formatLocale = false,
      helperText = '',
      hideClearIcon = false,
      icon = null,
      inputProps,
      invalid = false,
      keepFilled = false,
      keepFocused = false,
      label = '',
      multiline = false,
      name,
      noDropdownIcon = false,
      numeric = false,
      decimalScale = 2,
      onBlur,
      onChange,
      onClickClear,
      onFocus,
      onKeyDown,
      options,
      outline = false,
      rows,
      saving = false,
      shrink = false,
      startAdornment,
      style,
      type = 'text',
      unit = '',
      value = '',
      variant = 'TextField',
    },
    ref
  ) => {
    const { i18n } = useTranslation('common');
    const inputContainerRef = useRef(null);
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => inputRef.current);
    const [opened, setOpened] = useState(false);
    const [focused, setFocused] = useState(false || keepFocused);
    const themed = useStyles();
    const delayedSaving = useDelay(saving, 1000, false);
    const classes = classnames('ThemedTextField', className, themed.TextField, {
      [themed.TextField_Focused]: (focused && !disabled) || opened || keepFocused,
      [themed.TextField_Empty]: !focused && value === '' && outline,
      [themed.TextField_Filled]: (!focused && value !== '') || !outline || shrink,
      [themed.TextField_Disabled]: disabled,
      [themed.TextField_Invalid]: invalid,
      [themed.TextField_Outline]: outline,
      [themed.TextField_Multiline]: multiline,
    });

    const handleChange = (option) => (e) => {
      if (!onChange || disabled) return;
      switch (variant) {
        case 'TextAndDrop':
          if (!option) {
            onChange({ target: { ...e, inputType: type, value: e.currentTarget.value, name } });
          }
        // eslint-disable-next-line no-fallthrough
        case 'Dropdown':
          if (option) {
            onChange({ target: { ...e, inputType: type, ...option, name } });
            setOpened(false);
          }
          break;
        default:
          onChange({ target: { ...e, inputType: type, value: e.currentTarget.value, name } });
      }
    };

    const handleClickClear = () => {
      if (onClickClear) onClickClear({ target: { name, value: '', text: '' } });
    };

    const handleCloseMenu = () => {
      setOpened(false);
    };

    return (
      <div className={classes} style={style}>
        {icon &&
          React.cloneElement(icon, {
            ...icon.props,
            className: `${icon.className ? icon.className : ''} ThemedTextField__icon--start`,
          })}
        <div className="ThemedTextField__container" ref={inputContainerRef}>
          {unit && (focused || value !== '' || keepFocused) && (
            <span className="ThemedTextField__unit">[{unit}]</span>
          )}
          <Label
            active={focused || opened}
            filled={
              (focused && !disabled) ||
              value !== '' ||
              (opened && !disabled) ||
              shrink ||
              keepFilled
            }
            disabled={disabled}
            invalid={invalid}
            multiline={multiline}
          >
            {label}
          </Label>

          {(variant === 'Dropdown' || variant === 'TextAndDrop') && (
            <Menu
              classes={{ paper: themed.Dropdown__Menu }}
              style={MenuStyles}
              MenuListProps={{
                style: { minWidth: inputContainerRef.current?.parentElement.offsetWidth - 8 },
              }}
              anchorEl={inputContainerRef.current}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
                // horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
                // horizontal: 'center',
              }}
              open={opened && !disabled}
              onClose={(e, reason) => {
                if (reason === 'backdropClick') {
                  setOpened((o) => !o);
                  setFocused(false);
                }
              }}
            >
              {typeof children === 'function' ? children({ close: handleCloseMenu }) : children}
              {options &&
                options?.map((option) => (
                  <MenuItem
                    key={shortid()}
                    classes={{ root: themed.Dropdown__MenuItem }}
                    onClick={handleChange(option)}
                  >
                    {option.text}
                  </MenuItem>
                ))}
            </Menu>
          )}
          {numeric ? (
            <NumericFormat
              rows={rows}
              multiline={multiline}
              startAdornment={startAdornment}
              inputRef={inputRef}
              disabled={disabled}
              inputProps={inputProps}
              onFocus={(e) => {
                if (disabled) return;
                if (typeof onFocus === 'function') onFocus(e);
                if (variant === 'Dropdown') return;
                setFocused(true);
              }}
              onClick={() => {
                if (disabled) return;
                if (variant !== 'Dropdown') return;
                setOpened((open) => !open);
                setFocused(true);
              }}
              onBlur={(e) => {
                if (disabled) return;
                if (typeof onBlur === 'function') onBlur(e);
                setFocused(false);
              }}
              autoFocus={autoFocus}
              className={themed.TextField__input}
              onChange={handleChange()}
              onKeyDown={onKeyDown}
              value={formatLocale ? formatLocale(value, i18n.language) : value}
              customInput={InputBase}
              type="text"
              valueIsNumericString
              displayType="input"
              decimalSeparator={getDecimalSeparator(i18n.language)}
              decimalScale={decimalScale}
              allowNegative={false}
            />
          ) : (
            <InputBase
              rows={rows}
              multiline={multiline}
              startAdornment={startAdornment}
              inputRef={inputRef}
              disabled={disabled}
              inputProps={inputProps}
              onFocus={(e) => {
                if (disabled) return;
                if (typeof onFocus === 'function') onFocus(e);
                if (variant === 'Dropdown') return;
                setFocused(true);
              }}
              onClick={() => {
                if (disabled) return;
                if (variant !== 'Dropdown') return;
                setOpened((open) => !open);
                setFocused(true);
              }}
              onBlur={(e) => {
                if (disabled) return;
                if (typeof onBlur === 'function') onBlur(e);
                setFocused(false);
              }}
              autoFocus={autoFocus}
              type={type}
              className={themed.TextField__input}
              onChange={handleChange()}
              onKeyDown={onKeyDown}
              value={value}
            />
          )}

          {!!value && !disabled && !saving && !hideClearIcon && variant !== 'Dropdown' && (
            <IconButton tabIndex={clearIconTabIndex} size="small" onMouseDown={handleClickClear}>
              <Clear className={themed.TextField__icon_clear} size="small" />
            </IconButton>
          )}
          {(variant === 'Dropdown' || variant === 'TextAndDrop') && !disabled && !noDropdownIcon && (
            <IconButton
              disabled={!options || options.length === 0}
              size="small"
              className={themed.TextField__icon_button}
              onClick={() => {
                setOpened(!opened);
              }}
            >
              {opened ? (
                <ExpandLess className={themed.TextField__icon} />
              ) : (
                <ExpandMore className={themed.TextField__icon} />
              )}
            </IconButton>
          )}
          {helperText && <HelperText>{helperText}</HelperText>}
          <CSSTransition
            classNames="loader-transition"
            in={delayedSaving}
            timeout={{ enter: 0, exit: 2000 }}
            unmountOnExit
          >
            <Loader />
          </CSSTransition>
        </div>
      </div>
    );
  }
);

ThemedTextField.propTypes = {
  multiline: bool,
  label: string,
  icon: element,
  disabled: bool,
  unit: string,
  variant: oneOf(['TextField', 'Dropdown', 'TextAndDrop']),
  invalid: bool,
  helperText: string,
  saving: bool,
  children: oneOfType([node, func]),
  rows: number,
  autoFocus: bool,
  numeric: bool,
};

export default memo(ThemedTextField);
